import { Link, useParams } from "react-router-dom";
import { articles, categoryLabels, categoryPaths } from "@/mocks/articles";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatDate, formatViews } from "@/utils/format";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import ShareBar from "@/components/feature/ShareBar";
import SectionHeading from "@/components/feature/SectionHeading";

export default function DetailPage() {
  const { id } = useParams();
  const article = articles.find((item) => item.id === id);

  usePageMeta({
    title: article ? `${article.title} - 黑料吃瓜` : "内容未找到 - 黑料吃瓜",
    description: article ? article.excerpt : "该内容可能已下线或地址有误，返回黑料吃瓜首页浏览最新娱乐八卦与明星黑料。",
    keywords: article ? `${article.tags.join(",")},黑料吃瓜` : "黑料吃瓜,娱乐八卦,明星黑料",
  });

  if (!article) {
    return (
      <div className="max-w-[1320px] mx-auto px-4 md:px-6 py-20 text-center">
        <i className="ri-file-unknow-line text-4xl text-foreground-300" />
        <h1 className="font-heading text-2xl font-black text-foreground-900 mt-4">这条内容不存在</h1>
        <p className="text-[13.5px] text-foreground-500 mt-2">可能已下线、地址有误，或者我们正在核实新的线索。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 mt-6 px-5 h-10 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
        >
          <i className="ri-home-4-line" />
          回到首页
        </Link>
      </div>
    );
  }

  const related = articles
    .filter((item) => item.id !== article.id)
    .map((item) => ({
      item,
      score:
        (item.category === article.category ? 2 : 0) +
        item.tags.filter((tag) => article.tags.includes(tag)).length,
    }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((entry) => entry.item);

  const hotSide = articles.filter((item) => item.id !== article.id).sort((a, b) => b.views - a.views).slice(0, 5);

  return (
    <div className="max-w-[1320px] mx-auto px-4 md:px-6 py-5 md:py-7">
      <Breadcrumb
        items={[
          { label: categoryLabels[article.category], to: categoryPaths[article.category] },
          { label: "正文" },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 md:gap-8 mt-4">
        <article className="min-w-0">
          <header>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={categoryPaths[article.category]}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-accent-500 text-primary-950 text-[11.5px] font-bold cursor-pointer hover:bg-accent-600 transition-colors"
              >
                <i className="ri-newspaper-line" />
                {categoryLabels[article.category]}
              </Link>
              {article.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="px-2 py-1 rounded bg-secondary-100 text-secondary-800 text-[11.5px]">
                  #{tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-[23px] md:text-[33px] font-black text-foreground-950 leading-[1.4] mt-3">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 pb-4 border-b border-background-200 text-[12.5px] text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" />
                {article.author}
              </span>
              <span>{formatDate(article.date)}</span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatViews(article.views)} 阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-heart-3-line" />
                {formatViews(article.likes)} 互动
              </span>
              <div className="ml-auto">
                <ShareBar title={article.title} />
              </div>
            </div>
          </header>

          <figure className="mt-5">
            <div className="w-full h-[210px] md:h-[400px] rounded-lg overflow-hidden bg-background-200">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 黑料吃瓜`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <figcaption className="text-[12px] text-foreground-400 mt-2 text-center">
              图片为场景示意 · 内容为虚构演示
            </figcaption>
          </figure>

          <div className="mt-6 article-body text-[15px] leading-[2] text-foreground-700">
            {article.content.map((block, index) => {
              if (block.type === "h2") {
                return (
                  <h2
                    key={`block-${index}`}
                    className="font-heading text-[18px] md:text-[20px] font-black text-foreground-950 mt-7 mb-3 pl-3 border-l-[3px] border-accent-500"
                  >
                    {block.text}
                  </h2>
                );
              }
              if (block.type === "quote") {
                return (
                  <blockquote
                    key={`block-${index}`}
                    className="my-6 px-4 md:px-5 py-4 rounded-md bg-background-100 border-l-2 border-primary-400 text-[14px] md:text-[14.5px] leading-7 text-foreground-700"
                  >
                    {block.text}
                  </blockquote>
                );
              }
              if (block.type === "image") {
                return (
                  <figure key={`block-${index}`} className="my-6">
                    <div className="w-full h-[200px] md:h-[330px] rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={block.src}
                        alt={block.caption}
                        title={`${article.title} - 现场配图`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <figcaption className="text-[12px] text-foreground-400 mt-2 text-center">
                      {block.caption}
                    </figcaption>
                  </figure>
                );
              }
              return (
                <p key={`block-${index}`} className="text-justify">
                  {block.text}
                </p>
              );
            })}
          </div>

          <div className="mt-7 pt-5 border-t border-background-200">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-2.5 py-1 rounded-md bg-background-100 border border-background-200 text-[12px] text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg bg-background-100">
              <p className="text-[12.5px] text-foreground-600 leading-6">
                本文人物与事件均为虚构演示内容，不对应任何真实人物。请理性讨论，拒绝传播未经核实的信息。
              </p>
              <Link
                to="/contact"
                className="shrink-0 inline-flex items-center gap-1.5 px-4 h-9 rounded-md bg-primary-500 text-background-50 text-[12.5px] whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-megaphone-line" />
                提供新线索
              </Link>
            </div>
          </div>

          <section className="mt-8">
            <SectionHeading title="相关内容推荐" desc="基于相同分类与标签匹配" accent="primary" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </section>
        </article>

        <aside className="flex flex-col gap-5">
          <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200 bg-background-100">
              <h2 className="font-heading text-[15px] font-bold text-foreground-900 flex items-center gap-2">
                <i className="ri-fire-line text-accent-500" />
                热门阅读
              </h2>
            </div>
            <ol className="divide-y divide-background-200">
              {hotSide.map((item, index) => (
                <li key={item.id}>
                  <Link
                    to={`/detail/${item.id}`}
                    className="group flex items-start gap-3 p-3.5 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span
                      className={`w-5 h-5 shrink-0 rounded text-[11px] font-bold flex items-center justify-center mt-0.5 ${
                        index < 3 ? "bg-accent-500 text-primary-950" : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] font-medium text-foreground-800 leading-6 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </span>
                      <span className="text-[11.5px] text-foreground-400 mt-1 block">{item.date}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-lg border border-background-200 bg-accent-50 p-4">
            <h2 className="font-heading text-[15px] font-bold text-primary-900 flex items-center gap-2">
              <i className="ri-search-line text-accent-600" />
              继续追这条线
            </h2>
            <p className="text-[12.5px] text-foreground-600 leading-6 mt-2">
              想看同一人物的全部动态？用关键词搜索，能一次拉出相关报道与后续回应。
            </p>
            <Link
              to={`/search?q=${encodeURIComponent(article.tags[0])}`}
              className="inline-flex items-center gap-1 mt-3 text-[12.5px] font-medium text-accent-700 hover:text-accent-800 cursor-pointer"
            >
              搜索「{article.tags[0]}」
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="rounded-lg border border-background-200 overflow-hidden">
            <div className="w-full h-32 bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Night%20newsroom%20with%20glowing%20computer%20screens%2C%20scattered%20notes%20and%20warm%20desk%20lamps%2C%20empty%20chairs%2C%20cinematic%20wide%20angle%2C%20deep%20navy%20and%20amber%20tones%2C%20no%20readable%20text&width=700&height=400&seq=detail-newsroom-night&orientation=landscape"
                alt="黑料吃瓜编辑部夜间值班场景"
                title="黑料吃瓜编辑部 - 深夜核实线索"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-4">
              <h2 className="font-heading text-[14px] font-bold text-foreground-900">深夜的编辑部</h2>
              <p className="text-[12px] text-foreground-500 leading-6 mt-1.5">
                热点爆发时，我们优先确认三件事：时间、来源、当事方回应。缺一件，就标注为「待核实」。
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}